import React from "react";

type TokenKind = "name" | "addr" | "phone" | "city" | "menu" | "url" | "other";
type EvidenceInput =
  | string
  | { text: string; kind?: TokenKind; source?: "ocr" | "caption" | "oembed" | "other" };

type Props = {
  tokens: EvidenceInput[];
  max?: number;                // default 3
  dedupe?: boolean;            // default true
  onClickToken?: (t: { text: string; kind: TokenKind }) => void; // optional interactivity
  className?: string;
};

const KIND_CLASSES: Record<TokenKind, string> = {
  name:  "bg-emerald-500/15 text-emerald-300",
  addr:  "bg-sky-500/15     text-sky-300",
  phone: "bg-violet-500/15  text-violet-300",
  city:  "bg-cyan-500/15    text-cyan-300",
  menu:  "bg-amber-500/15   text-amber-300",
  url:   "bg-indigo-500/15  text-indigo-300",
  other: "bg-white/10       text-white/80",
};

function normalizeToken(t: EvidenceInput): { text: string; kind: TokenKind } {
  const raw = typeof t === "string" ? t : t?.text || "";
  const hint = typeof t === "string" ? "" : (t?.kind || "");
  // Strip common prefixes like "name:", "addr:", "phone:", "url:"
  const stripped = raw.replace(/^\s*(name|addr|address|phone|tel|url|token)\s*:\s*/i, "").trim();
  // Guess kind if not provided
  const kind: TokenKind =
    (hint as TokenKind) ||
    (/^\+?\d[\d\s().-]{6,}$/.test(stripped) ? "phone"
      : /(calle|av\.?|avenida|st\.?|ave\.?|blvd\.?|col\.?|cp\s*\d{4,6})/i.test(stripped) ? "addr"
      : /^https?:\/\//i.test(stripped) ? "url"
      : /cdmx|guadalajara|monterrey|tijuana|puebla|quer[eé]taro/i.test(stripped) ? "city"
      : "other");

  return { text: stripped, kind };
}

function ellipsize(s: string, max = 28) {
  return s.length <= max ? s : s.slice(0, max - 1) + "…";
}

export function EvidenceChips({
  tokens,
  max = 3,
  dedupe = true,
  onClickToken,
  className = "",
}: Props) {
  const normed = (tokens || []).map(normalizeToken).filter(t => t.text.length > 0);

  const unique = dedupe
    ? (() => {
        const seen = new Set<string>();
        return normed.filter((t) => {
          const k = `${t.kind}|${t.text.toLowerCase()}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      })()
    : normed;

  const items = unique.slice(0, Math.max(0, max));

  if (!items.length) return null;

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {items.map((t, i) => {
        const cls = KIND_CLASSES[t.kind] || KIND_CLASSES.other;
        const label = `Evidence ${t.kind}: ${t.text}`;
        const content = (
          <>
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-current" />
            <span className="truncate">{ellipsize(t.text)}</span>
          </>
        );

        return onClickToken ? (
          <button
            key={`${t.kind}-${t.text}-${i}`}
            type="button"
            onClick={() => onClickToken(t)}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${cls} focus:outline-none focus:ring-2 focus:ring-emerald-400`}
            aria-label={label}
            title={t.text}
          >
            {content}
          </button>
        ) : (
          <span
            key={`${t.kind}-${t.text}-${i}`}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${cls}`}
            aria-label={label}
            title={t.text}
          >
            {content}
          </span>
        );
      })}
    </div>
  );
}
